import type { IntlShape } from 'react-intl';
import moment from 'moment';

// Single source of truth for calendar-date (day-precision) handling.
//
// Rules:
// 1. A calendar day is identified by its literal YYYY-MM-DD. Server strings like
//    "2026-09-30T23:59:59" mean Sep 30 — moment.parseZone keeps the string's own
//    offset, so the day is never re-interpreted through the browser's timezone
//    (which is what causes off-by-one-day bugs).
// 2. Date objects representing a calendar day are anchored at UTC NOON of that day.
//    Noon reads back as the same day through both UTC-based APIs (toISOString) and
//    local-based APIs (Intl, getDate) for any real-world offset, and stays inside
//    the picked day when the backend truncates to a date in its own timezone.
//
// Always use these helpers instead of ad-hoc `new Date(...)` parsing, getTime()
// comparisons, or manual string splitting when only the day matters.

const DAY_FORMAT = 'YYYY-MM-DD';

export const MS_PER_DAY = 86400000;

/** Extract the literal 'YYYY-MM-DD' calendar day from any date-ish input, or '' if invalid. */
export const toDayString = (value: Date | string | number | null | undefined): string => {

    if (value === null || value === undefined || value === '') return '';

    // parseZone preserves the string's own wall-clock day; Dates/numbers read the
    // local calendar day (correct for `new Date()` and our UTC-noon anchors).
    const parsed = typeof value === 'string' ? moment.parseZone(value) : moment(value);
    return parsed.isValid() ? parsed.format(DAY_FORMAT) : '';

};

/** Parse any date-ish input into a Date anchored at UTC noon of its calendar day. */
export const parseDateOnly = (value: Date | string | number | null | undefined): Date | null => {

    const day = toDayString(value);
    return day ? moment.utc(day, DAY_FORMAT, true).hour(12).toDate() : null;

};

/** Whole days from `from` to `to` at day precision (positive when `to` is later). */
export const diffDays = (from: Date | string | number | null | undefined, to: Date | string | number | null | undefined): number => {

    const a = toDayString(from);
    const b = toDayString(to);
    return a && b ? moment.utc(b, DAY_FORMAT, true).diff(moment.utc(a, DAY_FORMAT, true), 'days') : 0;

};

/** Compare two values at day precision: -1 (a earlier), 0 (same day), 1 (a later). */
export const compareDays = (a: Date | string | number | null | undefined, b: Date | string | number | null | undefined): number => {

    const delta = diffDays(b, a);
    return delta === 0 ? 0 : (delta > 0 ? 1 : -1);

};

export const isSameDay = (a: Date | string | number | null | undefined, b: Date | string | number | null | undefined): boolean => !!toDayString(a) && toDayString(a) === toDayString(b);

/** True when `a` is on or before `b` at day precision (both required). */
export const isOnOrBeforeDay = (a: Date | string | number | null | undefined, b: Date | string | number | null | undefined): boolean => !!toDayString(a) && !!toDayString(b) && compareDays(a, b) <= 0;

/**
 * Display the calendar day to the user, or '' if invalid.
 * Pass the react-intl shape (from useIntl) wherever a component shows the date so it
 * renders in the active locale; the en-US fallback exists only for non-component code.
 */
export const formatDay = (value: Date | string | number | null | undefined, intl?: IntlShape): string => {

    const anchored = parseDateOnly(value);

    if (!anchored) return '';

    return intl
        ? intl.formatDate(anchored, { day: '2-digit', month: '2-digit', year: 'numeric' })
        : new Intl.DateTimeFormat('en-US', { day: '2-digit', month: '2-digit', year: 'numeric' }).format(anchored);

};
